// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Whether a draft can be played, and what to say when it cannot (M9-17, 11 §4).
//
// *"**Instant, in place, and never a separate mode you have to load into.** Press `Space` in the
// Layout workspace and the machine is live under the editor, running the real core at 1000 Hz."*
//
// **The gate is the real validator, not a lighter one.** A draft is a machine mid-edit: a ramp with
// one control point, a rule naming a lamp that was deleted a second ago, a flipper whose sweep
// crosses the drain. `loadMachine` is entitled to assume a machine that passed, and the failure of
// an unvalidated load is a stack trace in a console the author cannot see — from a button that
// looked like it did nothing.
//
// **Flood on.** 04 §16's note says the trap and reachability passes are off by default because they
// rasterise the playfield at ball radius, and that a client loading a machine the server already
// cleared should not pay for it. The Builder is the other case named in the same sentence: it pays,
// because the whole point of test play is finding out whether the ball can get around this machine,
// and a sealed pocket is exactly what an author would otherwise discover by watching a ball sit in
// it for a minute.
//
// Pure: no DOM, no runtime, no clock.

import {
  BUDGET_CODE,
  overBudget,
  validateMachine,
  type MachineDefinition,
  type ValidationIssue,
} from '../../core/ts/machine'

export interface TestPlayVerdict {
  /** Whether the machine can be put under a ball at all. */
  readonly playable: boolean
  /** What stops it, worst first. Empty when it is playable. */
  readonly stopping: readonly ValidationIssue[]
  /**
   * What is wrong but does not stop it.
   *
   * **Carried rather than swallowed**, because the warnings are the ones test play is *for*: a
   * device nothing can reach and a flipper pair with no centre drain are both legal machines and
   * both are things an author wants to feel rather than read.
   */
  readonly warnings: readonly ValidationIssue[]
  /** One sentence, for the button and the banner. */
  readonly says: string
}

/**
 * Whether this draft can be test-played.
 *
 * The warnings come back with it, so the panel can say "playable, and here is what you are about to
 * notice" instead of making the author read a separate report to find out.
 */
export function testPlayVerdict(definition: unknown): TestPlayVerdict {
  const report = validateMachine(definition as MachineDefinition, { flood: true })
  // **11 §8's budgets, at load** (M9-30): "enforced at save, again at publish, and again at load".
  // Test play *is* the load path in the Builder — the same `MachineRuntime` the arcade mounts — so a
  // machine over budget must not run here either, or an author would tune a machine on their own
  // desktop that no phone can open.
  const stopping = [
    ...report.errors,
    ...overBudget(definition).map((overrun) => ({
      path: overrun.path,
      code: BUDGET_CODE,
      message: overrun.message,
    })),
  ]
  const warnings = report.warnings
  if (stopping.length === 0) {
    return {
      playable: true,
      stopping: [],
      warnings,
      says:
        warnings.length === 0
          ? 'Ready to play.'
          : `Ready to play, with ${warnings.length} warning${warnings.length === 1 ? '' : 's'} you may notice while you do.`,
    }
  }
  const first = stopping[0] as ValidationIssue
  return {
    playable: false,
    stopping,
    warnings,
    says:
      stopping.length === 1
        ? `This machine cannot be played yet: ${first.path} — ${first.message}`
        : `This machine cannot be played yet. ${stopping.length} things stop it, starting with ${first.path} — ${first.message}`,
  }
}

/**
 * Whether a key press should start test play.
 *
 * **Space, and only when nobody is typing.** `isTyping` already exists for this and its comment in
 * `input/desktop.ts` names this exact case: *"a settings panel has text fields in it, and Space is
 * the plunger. Without this, typing a machine name into the Builder launches the ball."* The
 * Builder now has caption fields, note boxes and a dozen number spinners, so the same guard decides
 * whether Space means "play this" or means a space.
 *
 * Space **starts** and does not stop, which is not an omission: in a running game Space is the
 * plunger, so a Space that also stopped would take the machine away the first time an author tried
 * to launch a ball. Escape stops.
 */
export function startsTestPlay(key: string, typing: boolean): boolean {
  return key === ' ' && !typing
}

/** Whether a key press should end test play and give the editor back. */
export function stopsTestPlay(key: string): boolean {
  return key === 'Escape'
}
